import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';

@Component({
  selector: 'hlm-range-slider',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block', '[class.valued]': "thumb() === 'value'" },
  template: `
    <input
      type="range"
      class="track"
      [class.vertical]="orientation() === 'vertical'"
      [class.valued]="thumb() === 'value'"
      [min]="min()"
      [max]="max()"
      [step]="step()"
      [value]="value()"
      [disabled]="disabled()"
      [style.--fill]="fillPercent()"
      [style.--track-bg]="trackBackground()"
      [style.--track-progress]="progressBackground()"
      [attr.aria-label]="ariaLabel()"
      [attr.data-testid]="testId()"
      (input)="onInput($event)"
      (change)="onCommit($event)"
    />
    @if (thumb() === 'value') {
      @for (at of dots(); track at) {
        <span class="dot" [class.vertical]="orientation() === 'vertical'" [style.--at]="at" aria-hidden="true"></span>
      }
      <span class="bubble" [class.vertical]="orientation() === 'vertical'" [style.--at]="ratio()" aria-hidden="true">{{
        live()
      }}</span>
    }
  `,
  styles: [
    `
      .track {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 18px;
        margin: 0;
        background: transparent;
        outline: none;
        cursor: pointer;
      }
      .track::-webkit-slider-runnable-track {
        height: 3px;
        border-radius: 2px;
        background: var(
          --track-bg,
          linear-gradient(to right, var(--acc) 0%, var(--acc) var(--fill), var(--line2) var(--fill), var(--line2) 100%)
        );
      }
      .track::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 12px;
        height: 12px;
        margin-top: -4.5px;
        border-radius: 50%;
        background: var(--panel);
        border: 1.5px solid var(--acc);
        transition: transform var(--t-fast) var(--ease-smooth);
      }
      .track::-moz-range-track {
        height: 3px;
        border-radius: 2px;
        background: var(--track-bg, var(--line2));
      }
      .track::-moz-range-progress {
        height: 3px;
        border-radius: 2px;
        background: var(--track-progress, var(--acc));
      }
      .track::-moz-range-thumb {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: var(--panel);
        border: 1.5px solid var(--acc);
      }
      /* Vendor thumb pseudo-elements never share a selector list — an unknown one voids the whole rule. */
      .track:hover:not(:disabled)::-webkit-slider-thumb {
        transform: scale(1.15);
      }
      .track:hover:not(:disabled)::-moz-range-thumb {
        transform: scale(1.15);
      }
      .track:focus-visible::-webkit-slider-thumb {
        box-shadow: 0 0 0 3px var(--acc-line);
      }
      .track:focus-visible::-moz-range-thumb {
        box-shadow: 0 0 0 3px var(--acc-line);
      }
      .track:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
      /* Вертикальная ось — штатная writing-mode-раскладка range (MDN): значение растёт снизу вверх,
         поэтому и заливка идёт снизу. Дорожка и бегунок лишь меняют оси местами. */
      .track.vertical {
        writing-mode: vertical-lr;
        direction: rtl;
        width: calc(var(--spacing) * 4.5);
        height: var(--size-full);
      }
      .track.vertical::-webkit-slider-runnable-track {
        width: calc(var(--spacing) * 0.75);
        height: var(--size-full);
        background: var(
          --track-bg,
          linear-gradient(to top, var(--acc) 0%, var(--acc) var(--fill), var(--line2) var(--fill), var(--line2) 100%)
        );
      }
      .track.vertical::-webkit-slider-thumb {
        margin-top: 0;
        margin-left: calc(var(--spacing) * -1.125);
      }
      .track.vertical::-moz-range-track {
        width: calc(var(--spacing) * 0.75);
        height: var(--size-full);
      }
      .track.vertical::-moz-range-progress {
        width: calc(var(--spacing) * 0.75);
        height: auto;
      }
      /* Valued thumb: the transparent native thumb keeps hit area + keyboard; the bubble paints it with the value.
         Both engines travel the thumb centre from half a thumb to (length − half a thumb). */
      :host(.valued) {
        --thumb: calc(var(--spacing) * 5.5);
        --dot: var(--spacing);
        position: relative;
      }
      .track.valued {
        height: var(--thumb);
      }
      .track.valued.vertical {
        width: var(--thumb);
        height: var(--size-full);
      }
      .track.valued::-webkit-slider-thumb {
        width: var(--thumb);
        height: var(--thumb);
        margin-top: calc((var(--spacing) * 0.75 - var(--thumb)) / 2);
        border: 0;
        background: transparent;
        box-shadow: none;
      }
      .track.valued.vertical::-webkit-slider-thumb {
        margin-top: 0;
        margin-left: calc((var(--spacing) * 0.75 - var(--thumb)) / 2);
      }
      .track.valued::-moz-range-thumb {
        width: var(--thumb);
        height: var(--thumb);
        border: 0;
        background: transparent;
        box-shadow: none;
      }
      .bubble,
      .dot {
        position: absolute;
        pointer-events: none;
        border-radius: var(--r-round);
      }
      .bubble {
        top: calc((var(--size-full) - var(--thumb)) / 2);
        left: calc(var(--at) * (var(--size-full) - var(--thumb)));
        display: grid;
        place-items: center;
        width: var(--thumb);
        height: var(--thumb);
        background: var(--on-media);
        color: var(--plate-solid);
        box-shadow: 0 0 0 var(--spacing) var(--acc-soft);
        font-size: var(--text-control);
        font-weight: var(--font-weight-semibold);
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
      .dot {
        top: calc((var(--size-full) - var(--dot)) / 2);
        left: calc(var(--at) * (var(--size-full) - var(--thumb)) + (var(--thumb) - var(--dot)) / 2);
        width: var(--dot);
        height: var(--dot);
        background: var(--c3);
      }
      .bubble.vertical {
        top: auto;
        left: calc((var(--size-full) - var(--thumb)) / 2);
        bottom: calc(var(--at) * (var(--size-full) - var(--thumb)));
      }
      .dot.vertical {
        top: auto;
        left: calc((var(--size-full) - var(--dot)) / 2);
        bottom: calc(var(--at) * (var(--size-full) - var(--thumb)) + (var(--thumb) - var(--dot)) / 2);
      }
      .track.valued:focus-visible ~ .bubble {
        outline: calc(var(--spacing) / 4) solid var(--acc);
        outline-offset: var(--spacing);
      }
      .track.valued:disabled ~ span {
        opacity: 0.5;
      }
    `,
  ],
})
export class RangeSliderComponent {
  readonly min = input(0);
  readonly max = input(100);
  readonly step = input(1);
  readonly value = input(0);
  readonly disabled = input(false);
  /** Вертикальный слайдер растёт снизу вверх и берёт высоту у хоста — задай ему её. */
  readonly orientation = input<'horizontal' | 'vertical'>('horizontal');
  readonly ariaLabel = input<string | null>(null);
  readonly testId = input<string | null>(null);
  /** Paints the track with caller-owned DATA (a colour-space spectrum), replacing the accent fill. */
  readonly trackBackground = input<string | null>(null);
  /** `value` = large round thumb carrying the current value, step dots ahead of it on a short ladder. */
  readonly thumb = input<'dot' | 'value'>('dot');

  readonly valueChange = output<number>();
  /** Конец жеста — нативный `change` (отпускание ручки/шаг с клавиатуры), для снапов «на отпускании». */
  readonly valueCommit = output<number>();

  // A data-painted track owns the whole width — Firefox's progress overlay must not repaint its left part.
  protected readonly progressBackground = computed(() => (this.trackBackground() ? 'transparent' : null));

  // The valued thumb follows the native range even when the host does not echo `value` back mid-gesture.
  protected readonly live = linkedSignal(() => this.value());

  protected readonly ratio = computed(() => {
    const range = this.max() - this.min();
    if (range <= 0) return 0;
    const current = this.thumb() === 'value' ? this.live() : this.value();
    return Math.max(0, Math.min(1, (current - this.min()) / range));
  });

  protected readonly fillPercent = computed(() => `${this.ratio() * 100}%`);

  /** Positions (0…1) of the steps still ahead of the valued thumb; a long ladder gets none. */
  protected readonly dots = computed(() => {
    const min = this.min();
    const range = this.max() - min;
    const step = this.step();
    if (this.thumb() !== 'value' || range <= 0 || range > 12 || step <= 0) return [];
    const ahead: number[] = [];
    for (let v = min; v <= this.max(); v += step) if (v > this.live()) ahead.push((v - min) / range);
    return ahead;
  });

  protected onInput(event: Event): void {
    const raw = Number((event.target as HTMLInputElement).value);
    const next = Number.isFinite(raw) ? raw : this.min();
    this.live.set(next);
    this.valueChange.emit(next);
  }

  protected onCommit(event: Event): void {
    const raw = Number((event.target as HTMLInputElement).value);
    this.valueCommit.emit(Number.isFinite(raw) ? raw : this.min());
  }
}
